import { action, makeObservable, observable } from 'mobx';
import { ZodiacApi } from '@/api';
import { userStore } from '@/store/base';
import { AACheckoutConst } from '@/helper/constants';
import { Log } from '@/helper/log';
import { BundleParser, PlainAddonItemsGenerator } from './parse';
import CabinDataManager from '../cabin-data-manager';
import { CABIN_CLASS } from '@/helper/constants/aa-checkout';
import { globalAlertStore } from '@/store/common';
import globalLoadingStore from '@/store/common/global-loading';
import i18n from '@/helper/i18n';
import { navigateBack } from '@/helper/util';
import * as FlatbedMergeUtils from './flatbed-merge';

const t = i18n.chain;

const METHOD_NAMES = {
  LOAD_DATA: 'loadData',
  RESET_DATA: 'resetData',
} as const;

export default class AncillaryBundleStore {
  private CLASS_NAME = 'AncillaryBundleStore';
  private cabinManager: CabinDataManager;

  @observable bundleData: AACheckout.BundleItem[] = [];
  @observable startPrice: number | null = null;
  @observable totalAmount = 0;
  @observable plainAddonItems: AACheckout.PlainAddonItem[] = [];
  @observable plainAddonItemsForCheckout: AACheckout.PlainAddonItem[] = [];
  @observable selectedBundleData: any;
  @observable comparisonData: any = null;
  @observable componentConfig = {
    isPremiumFlatbed: false,
    disableSelect: false,
  };
  @observable bundleInfluenceData: {
    [key in AACheckoutConst.BundleAncillaryTypeEnum]?: AACheckout.BundleInfluenceData;
  } = {};
  @observable plainAddonItemMap: {
    [key in AACheckoutConst.BundleAncillaryTypeEnum]?: AACheckout.PlainAddonItem[];
  };
  @observable isBundleValidMap: {
    [key in AACheckoutConst.BundleAncillaryTypeEnum]?: boolean;
  } = {
    meals: true,
    seat: true,
  };
  @observable dataLoading = false;
  @observable isDataLoaded = false;
  @observable isFlatbedMerged = false;
  @observable originBundleData: any = null;

  bundleParser: BundleParser;
  plainAddonItemsGenerator: PlainAddonItemsGenerator;

  conversionRates: AACheckout.ConversionRates = null;
  paxTitles: AACheckout.PaxTitle[] = [];
  tripsInfo: TripsInfo = null;

  constructor() {
    makeObservable(this);
    this.cabinManager = new CabinDataManager(
      this.CLASS_NAME,
      this.fetchBundleData,
    );
  }

  private handleBundleAutoSelection(bundleData: AACheckout.BundleItem[]) {
    // Find bundle with included depart flight
    const departBundle = bundleData.find(
      (bundle) => bundle.departFlight?.isIncluded,
    );
    // Find bundle with included return flight
    const returnBundle = bundleData.find(
      (bundle) => bundle.returnFlight?.isIncluded,
    );

    if (departBundle || returnBundle) {
      this.onSelectedBundle({
        departFlight: departBundle?.departFlight,
        returnFlight: returnBundle?.returnFlight,
      });
    }
  }

  @action
  private async loadDataCore(): Promise<void> {
    if (
      !(
        (this.tripsInfo &&
          this.conversionRates &&
          this.conversionRates.length > 0) ||
        (this.paxTitles && this.paxTitles.length > 0)
      )
    ) {
      return;
    }

    this.dataLoading = true;

    const result = await this.cabinManager.getCabinData(this.tripsInfo);
    const originalPageData = result.pageData;

    if (this.isEmptyAncillary(originalPageData)) {
      this.handleBundleLoadError();
      return;
    }

    this.bundleParser = new BundleParser(
      originalPageData,
      this.conversionRates,
      this.paxTitles.length,
    );
    const { startPrice, bundleData, comparisonData, originBundleData } =
      this.bundleParser.parse();

    this.startPrice = startPrice;
    this.bundleData = bundleData;
    this.comparisonData = comparisonData;
    this.originBundleData = originBundleData;
    this.dataLoading = false;
    this.isDataLoaded = true;
  }

  @action
  loadData = async () => {
    if (this.isDataLoaded || this.dataLoading) return;

    try {
      await this.loadDataCore();

      if (this.isDataLoaded) {
        this.handleBundleAutoSelection(this.bundleData);
      }
    } catch (e) {
      this.handleBundleLoadError();
      Log.errorData(e, this.CLASS_NAME, METHOD_NAMES.LOAD_DATA);
    }
  };

  isEmptyAncillary(res) {
    return !res.ancillary?.bundle;
  }

  @action
  onSelectedBundle = async (selectedBundleData) => {
    this.selectedBundleData = selectedBundleData;
    this.generatePlainAddonItems();

    //distribute ancillaries
    this.bundleInfluenceData = this.distributeAncillaries(selectedBundleData);
  };

  @action
  clearMealsValidationStatus = () => {
    this.isBundleValidMap.meals = true;
  };

  @action
  clearSeatValidationStatus = () => {
    this.isBundleValidMap.seat = true;
  };

  handleBundleLoadError = () => {
    if (
      this.tripsInfo.departCabinClass === CABIN_CLASS.C ||
      this.tripsInfo.returnCabinClass === CABIN_CLASS.C
    ) {
      globalAlertStore.showAlert({
        translation: {
          title: t.error.aaLoadDataError.premiumFlatBedError.title,
          subTitle: t.error.aaLoadDataError.premiumFlatBedError.message,
          primaryButtonTitle: t.error.aaCheckoutErrors._buttonTitle,
        },
        primaryHandleClick: navigateBack,
      });
    }
  };

  @action
  distributeAncillaries = (selectedBundleData) => {
    const ancillaries: {
      [key in AACheckoutConst.BundleAncillaryTypeEnum]?: AACheckout.BundleInfluenceData;
    } = {};
    if (!this.bundleParser) {
      return;
    }

    if (selectedBundleData.departFlight) {
      this.bundleParser.processIncludedSsrs(
        selectedBundleData.departFlight,
        AACheckoutConst.FlightKeyEnum.DEPART_FLIGHT,
        ancillaries,
      );
    }

    if (selectedBundleData.returnFlight) {
      this.bundleParser.processIncludedSsrs(
        selectedBundleData.returnFlight,
        AACheckoutConst.FlightKeyEnum.RETURN_FLIGHT,
        ancillaries,
      );
    }

    return ancillaries;
  };

  @action
  updatePlainAddonItemMap(plainAddonItemMap: {
    [key in AACheckoutConst.BundleAncillaryTypeEnum]?: AACheckout.PlainAddonItem[];
  }) {
    this.plainAddonItemMap = plainAddonItemMap;
    this.generatePlainAddonItems();
  }

  generatePlainAddonItems = () => {
    this.plainAddonItemsGenerator = new PlainAddonItemsGenerator(
      this.paxTitles,
      this.plainAddonItemMap,
      this.selectedBundleData,
    );
    const { plainAddonItemsForCheckout, plainAddonItems } =
      this.plainAddonItemsGenerator.generate();

    const processedPlainAddonItems =
      this.processPremiumFlatbedItems(plainAddonItems);

    this.plainAddonItemsForCheckout = plainAddonItemsForCheckout;
    this.plainAddonItems = processedPlainAddonItems;

    this.totalAmount = this.plainAddonItems
      .map((i) => i.price)
      .reduce((a, b) => a + b, 0);
  };

  private processPremiumFlatbedItems(
    items: AACheckout.PlainAddonItem[],
  ): AACheckout.PlainAddonItem[] {
    return items.map((item) => {
      if (item.properties?.bundleName === AACheckoutConst.FLATBED_BUNDLE_NAME) {
        return {
          ...item,
          price: 0,
        };
      }
      return item;
    });
  }

  generateCheckoutAddonItems = () => {
    return this.plainAddonItemsForCheckout.map((addonItem) => {
      return {
        id: addonItem.id,
        category: addonItem.category,
        designator: {
          departureStation: addonItem.departureStation,
          arrivalStation: addonItem.arrivalStation,
          departureTime: null,
          arrivalTime: null,
          departureTimeUtc: null,
          arrivalTimeUtc: null,
          arrivalTerminal: null,
          departureTerminal: null,
        },
        properties: addonItem.properties,
        passengerReferenceId: addonItem.passengerReferenceId,
      };
    });
  };

  @action
  validate() {
    if (!this.selectedBundleData) {
      return true;
    }

    if (this.plainAddonItemsGenerator) {
      const validationResult = this.plainAddonItemsGenerator.validationResult;

      // find first key equal false
      const firstFalseKey = Object.keys(validationResult).find(
        (key) => !validationResult[key],
      );

      if (firstFalseKey) {
        // set other keys equal true
        this.isBundleValidMap = Object.keys(validationResult).reduce(
          (acc, key) => {
            acc[key] = key === firstFalseKey ? false : true;
            return acc;
          },
          {},
        );
      } else {
        // if have not false key
        this.isBundleValidMap = { ...validationResult };
      }

      return !firstFalseKey;
    }

    return true;
  }

  private fetchBundleData = async (tripsInfo: TripsInfo): Promise<any> => {
    const tokenInfo = await userStore.getFlushedTokenInfo();
    let response = await ZodiacApi.getPageData(
      tripsInfo,
      tokenInfo,
      ['bundle'],
      ['ancillary'],
    );

    // 1. cache economy data
    FlatbedMergeUtils.cacheEconomyData(tripsInfo, response);

    // 2. merge economy data if business cabin
    if (FlatbedMergeUtils.needsMerge(tripsInfo)) {
      response = FlatbedMergeUtils.mergeWithEconomyCache(response, tripsInfo);
      this.isFlatbedMerged = true;
    } else {
      this.isFlatbedMerged = false;
    }

    return response;
  };

  @action
  resetData = async () => {
    try {
      globalLoadingStore.open();

      // Save current user selection
      const savedSelection = this.selectedBundleData;

      // Reload data
      await this.loadDataCore();

      // restore bundle selection
      if (this.isFlatbedMerged) {
        const newSelectedBundleData =
          FlatbedMergeUtils.restoreSelectedBundleWithNewData(
            this.bundleData,
            savedSelection,
          );

        this.onSelectedBundle(newSelectedBundleData);
      }
    } catch (e) {
      Log.errorData(e, this.CLASS_NAME, METHOD_NAMES.RESET_DATA);
    } finally {
      globalLoadingStore.close();
    }
  };
}
